import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusCircle, Pencil, Loader2, Check, X } from 'lucide-react';
import { createSystemUserSchema, editSystemUserSchema } from '@tyto/shared';

// Define the user type
interface User {
  id: string;
  email: string;
  name: string;
  systemAdmin: boolean;
  teamId?: string;
  createdAt: string;
  updatedAt: string;
}

type UserFormValues = z.infer<typeof createSystemUserSchema>;
type EditSystemUserFormValues = z.infer<typeof editSystemUserSchema>;


const AdminUsers: React.FC = () => {
  const queryClient = useQueryClient();


  // Fetch users
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetch('/api/user');
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      return response.json() as Promise<User[]>;
    },
  });



  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Users</h1>
        <Link to="/app/admin/users/create">
          <Button>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </Link>

      </div>




      <p className="text-muted-foreground">Manage all users in the system.</p>
      <div
        className="space-y-4"
      >

        {isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-800">
            Failed to load users. Please try again.
          </div>
        ) : (
          <div className="space-y-4">
            {users && users.length > 0 ? (
              <div className="grid gap-4">
                {users.map((user) => (
                  <Card key={user.id}>
                    <CardHeader>
                      <div className="flex justify-between">
                        <CardTitle>{user.name}</CardTitle>
                        <div className="flex gap-2">
                          <Link to={`/app/admin/user/${user.id}`}>
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                          </Link>
                          <Link to={`/app/admin/user/${user.id}/edit`}>
                            <Button variant="ghost" size="sm">
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </Link>
                        </div>
                      </div>
                      <CardDescription>{user.email}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm font-medium">Role:</span>
                          <div className="flex items-center mt-1">
                            {user.systemAdmin ? (
                              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-md flex items-center">
                                <Check className="h-3 w-3 mr-1" />
                                System Admin
                              </div>
                            ) : (
                              <div className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-md flex items-center">
                                Regular User
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-sm font-medium">Created:</span>
                          <p className="text-sm text-muted-foreground">{new Date(user.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                No users found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { AdminUsers };
